// Coaching contextual de utilidad para actividades económicas
// Centraliza la lógica de rangos, frases y tooltips

export interface CoachingUtilidad {
  etiqueta: string;
  frase: string;
  tooltip: string;
  bgClass: string;
  borderClass: string;
  textClass: string;
  badgeClass: string;
  barFillClass: string;
}

/**
 * Calcula el porcentaje de utilidad potencial (si se vende todo el stock)
 * Fórmula: ((ingresoMaximo - totalCostos) / ingresoMaximo) × 100
 */
export function calcularPorcentajeUtilidadPotencial(
  ingresoMaximo: number,
  totalCostos: number
): number {
  if (ingresoMaximo <= 0) return 0;
  const utilidad = ingresoMaximo - totalCostos;
  if (utilidad <= 0) return 0;
  return Math.round((utilidad / ingresoMaximo) * 100 * 100) / 100;
}

/**
 * Retorna coaching contextual según el porcentaje de utilidad
 * @param porcentaje - Porcentaje de utilidad (potencial o real según contexto)
 * @param hayDatos - Si hay costos e ingresos registrados
 * @param hayDeficit - Si los costos superan los ingresos
 */
export function getCoachingUtilidad(
  porcentaje: number,
  hayDatos: boolean,
  hayDeficit: boolean
): CoachingUtilidad {
  // Sin datos suficientes
  if (!hayDatos) {
    return {
      etiqueta: '',
      frase: 'Registra gastos e ingresos para evaluar la rentabilidad',
      tooltip: '',
      bgClass: 'bg-slate-50',
      borderClass: 'border-slate-200',
      textClass: 'text-slate-600',
      badgeClass: 'bg-slate-100 text-slate-600',
      barFillClass: 'bg-slate-400'
    };
  }

  // Déficit: costos superan ingresos
  if (hayDeficit) {
    return {
      etiqueta: 'Déficit',
      frase: 'Prioriza cobrar ventas pendientes',
      tooltip: 'Los gastos superan los ingresos. Acciones: 1) Cobra los pedidos pendientes de pago. 2) Revisa si hay gastos que se puedan reducir o eliminar. Ej: si compraste envases de mas, evalua devolverlos o usarlos en otra actividad.',
      bgClass: 'bg-red-50',
      borderClass: 'border-red-200',
      textClass: 'text-red-700',
      badgeClass: 'bg-red-100 text-red-700',
      barFillClass: 'bg-red-400'
    };
  }

  // 0% – 29%: Baja
  if (porcentaje < 30) {
    return {
      etiqueta: 'Baja',
      frase: 'Busca reducir gastos o subir precios',
      tooltip: 'Una utilidad bajo 30% indica que gran parte del ingreso se va en gastos. Acciones: 1) Compra insumos por mayor para obtener descuento. 2) Evalua si el precio de venta cubre bien los gastos. Ej: si un producto cuesta $500 en materiales y lo vendes a $700, tu margen es solo 29%. Subir a $800 lo llevaria a 37%.',
      bgClass: 'bg-orange-50',
      borderClass: 'border-orange-200',
      textClass: 'text-orange-700',
      badgeClass: 'bg-orange-100 text-orange-700',
      barFillClass: 'bg-orange-400'
    };
  }

  // 30% – 49%: Moderada
  if (porcentaje < 50) {
    return {
      etiqueta: 'Moderada',
      frase: 'Optimiza cantidades y reduce desperdicio',
      tooltip: 'Estás cerca de la rentabilidad ideal. Acciones: 1) Revisa que las materias primas sean de calidad acorde al precio. 2) Ajusta las cantidades de producción para minimizar sobrantes. Ej: si sobran 5 unidades de 50, eso es 10% de desperdicio que baja tu margen.',
      bgClass: 'bg-amber-50',
      borderClass: 'border-amber-200',
      textClass: 'text-amber-700',
      badgeClass: 'bg-amber-100 text-amber-700',
      barFillClass: 'bg-amber-400'
    };
  }

  // 50% – 59%: Buena
  if (porcentaje < 60) {
    return {
      etiqueta: 'Buena',
      frase: '¡Rentable! Estandariza el proceso',
      tooltip: '¡Felicidades! Tu actividad es rentable. Acciones: 1) Documenta la receta o proceso de elaboración. 2) Anota la forma de venta que funcionó. Ej: si vendes empanadas, escribe las cantidades exactas de cada ingrediente y el método de cocción para que cualquier persona pueda replicarlo.',
      bgClass: 'bg-emerald-50',
      borderClass: 'border-emerald-200',
      textClass: 'text-emerald-700',
      badgeClass: 'bg-emerald-100 text-emerald-700',
      barFillClass: 'bg-emerald-500'
    };
  }

  // 60% – 79%: Muy buena
  if (porcentaje < 80) {
    return {
      etiqueta: 'Muy buena',
      frase: 'Evalúa aumentar el volumen',
      tooltip: 'Excelente margen de ganancia. Acciones: 1) Considera producir más unidades en la próxima actividad. 2) Mantén la misma calidad y proveedor. Ej: si vendiste 50 unidades con 65% de utilidad, prueba con 80 unidades la próxima vez manteniendo el mismo precio y proveedor.',
      bgClass: 'bg-green-50',
      borderClass: 'border-green-200',
      textClass: 'text-green-700',
      badgeClass: 'bg-green-100 text-green-700',
      barFillClass: 'bg-green-500'
    };
  }

  // 80% – 89%: Excepcional
  if (porcentaje < 90) {
    return {
      etiqueta: 'Excepcional',
      frase: 'Caso de éxito: comparte con otras sedes',
      tooltip: 'Margen excepcional. Acciones: 1) Comparte tu experiencia y receta con otras sedes. 2) Evalúa si puedes ofrecer este producto de forma recurrente. Ej: si lograste 85% de utilidad vendiendo jabones artesanales, otras sedes podrían replicar tu modelo con los mismos proveedores.',
      bgClass: 'bg-emerald-50',
      borderClass: 'border-emerald-200',
      textClass: 'text-emerald-800',
      badgeClass: 'bg-emerald-100 text-emerald-800',
      barFillClass: 'bg-emerald-600'
    };
  }

  // ≥ 90%: Verificar costos
  return {
    etiqueta: 'Gastos pendientes?',
    frase: 'Revisa si faltan gastos por registrar',
    tooltip: 'Una utilidad sobre 90% es muy inusual en actividades comerciales. Esto puede significar: 1) Faltan gastos por registrar (transporte, envases, gas, materiales de empaque, etc.). 2) Los insumos fueron donados o subsidiados. Si es asi, excelente! Pero si no, revisa que todos los gastos esten registrados para tener un calculo real de la rentabilidad.',
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-300',
    textClass: 'text-blue-700',
    badgeClass: 'bg-blue-100 text-blue-700',
    barFillClass: 'bg-blue-400'
  };
}
